export type ServiceLogo = { src: string; alt: string }

export type OpsId = 'marketing-ops' | 'sales-ops' | 'data-ops'

function favicon(domain: string, alt: string): ServiceLogo {
  return { src: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`, alt }
}

export const SMART_SERVICES_CRM = {
  eyebrow: 'SmartOps',
  title: 'SmartOps — the operations',
  titleEm: 'behind AI-driven growth.',
  lead: 'Built, connected, and kept running. Senior work, direct access, no handoffs.',
} as const

export const SMART_SERVICES_HERO = {
  title: 'Marketing, sales, and data systems',
  titleEm: '',
  sub: SMART_SERVICES_CRM.lead,
  primaryCta: 'Get a project quote',
  primaryHref: '#get-quote',
  bgSrc: '/images/smart-ops/hero.jpg',
} as const

export type CoverIconName =
  | 'funnel'
  | 'build'
  | 'paid'
  | 'seo'
  | 'cro'
  | 'ecommerce'
  | 'track'
  | 'attrib'
  | 'consent'
  | 'report'

export type StandItem = {
  n: string
  title: string
  body: string
  go: string
  href: string
}

export type CoverItem = {
  n: string
  icon: CoverIconName
  title: string
  body: string
  logos?: ServiceLogo[]
}

export const SMART_OPS_SEGMENTS: {
  id: OpsId
  n: string
  label: string
  sentence: string
  tabSub: string
  catalogLabel: string
  catalogHref: string
  stand: StandItem[]
  covers: CoverItem[]
}[] = [
  {
    id: 'marketing-ops',
    n: '01',
    label: 'Marketing ops',
    sentence: 'Ads, SEO, CRO, AI search. Run on real outcome data.',
    tabSub: 'Ads, SEO, CRO, AI search',
    catalogLabel: 'See all marketing ops services',
    catalogHref: '/services?segment=marketing-ops',
    stand: [
      {
        n: '01',
        title: 'No campaigns',
        body: 'Nothing live yet. You need the stack built before spend starts.',
        go: 'Implementation',
        href: '#get-quote',
      },
      {
        n: '02',
        title: 'Running but untracked',
        body: 'Ads are on. You cannot see which ones make money.',
        go: 'Audit',
        href: '/audit',
      },
      {
        n: '03',
        title: 'Agency runs it',
        body: 'Someone else owns the accounts. You want the work in-house, or a second pair of eyes.',
        go: 'Second opinion',
        href: '#get-quote',
      },
      {
        n: '04',
        title: 'Need it managed',
        body: 'The system works. You need someone senior to keep it moving month to month.',
        go: 'Retainer',
        href: '#get-quote',
      },
    ],
    covers: [
      {
        n: '01',
        icon: 'paid',
        title: 'Paid',
        body: 'Google Ads, Meta, MS Ads, and others. Built, audited, and judged on revenue, not clicks.',
        logos: [
          { src: '/images/googleads.png', alt: 'Google Ads' },
          { src: '/images/facebook.png', alt: 'Meta' },
          { src: '/images/microsoft.png', alt: 'Microsoft Ads' },
          { src: '/images/merchant.png', alt: 'Merchant Center' },
          { src: '/images/linkedin.png', alt: 'LinkedIn Ads' },
          { src: '/images/tiktok.png', alt: 'TikTok Ads' },
        ],
      },
      {
        n: '02',
        icon: 'seo',
        title: 'SEO',
        body: 'Technical SEO so the right pages get found and stay found.',
        logos: [
          { src: '/images/gsc.png', alt: 'Google Search Console' },
          favicon('semrush.com', 'Semrush'),
          favicon('ahrefs.com', 'Ahrefs'),
          favicon('screamingfrog.co.uk', 'Screaming Frog'),
        ],
      },
      {
        n: '03',
        icon: 'cro',
        title: 'CRO',
        body: 'A/B testing, landing page, and funnel tests that lift conversion without buying more traffic.',
        logos: [
          favicon('optimizely.com', 'Optimizely'),
          favicon('vwo.com', 'VWO'),
          { src: '/images/posthog.png', alt: 'PostHog' },
        ],
      },
      {
        n: '04',
        icon: 'seo',
        title: 'AI search',
        body: 'Visibility in Google AI Overviews, ChatGPT, and other answer engines so the right pages get cited.',
        logos: [
          { src: '/images/logo-chatgpt.svg', alt: 'ChatGPT' },
          { src: '/images/gemini.svg', alt: 'Gemini' },
          { src: '/images/claude.png', alt: 'Claude' },
        ],
      },
    ],
  },
  {
    id: 'sales-ops',
    n: '02',
    label: 'Sales ops',
    sentence: 'CRM, routing, follow-up, speed-to-lead.',
    tabSub: 'CRM, routing, follow-up',
    catalogLabel: 'See all sales ops services',
    catalogHref: '/services?segment=sales-ops',
    stand: [
      {
        n: '01',
        title: 'New to the CRM',
        body: 'Just signed up and need someone to set it up right.',
        go: 'Implementation',
        href: '#get-quote',
      },
      {
        n: '02',
        title: 'Just bought it',
        body: "You bought it but haven't launched — you need onboarding to get going.",
        go: 'Onboarding',
        href: '#get-quote',
      },
      {
        n: '03',
        title: "Portal's a mess",
        body: "You've got a CRM, but it's misconfigured and no one's really using it.",
        go: 'Audit / Reset',
        href: '/audit',
      },
      {
        n: '04',
        title: 'Up and running',
        body: 'The CRM is live, and you need someone to keep it evolving month to month.',
        go: 'Retainer',
        href: '#get-quote',
      },
    ],
    covers: [
      {
        n: '01',
        icon: 'funnel',
        title: 'Funnel and data design',
        body: 'Lifecycle stages, entry and exit criteria per stage, ownership and handoff rules, field definitions, and a deduplication plan.',
      },
      {
        n: '02',
        icon: 'build',
        title: 'CRM build',
        body: 'Pipeline setup or restructure, routing and assignment, workflow automation, email sequences, and speed-to-lead.',
        logos: [
          { src: '/images/logo-hubspot.svg', alt: 'HubSpot' },
          { src: '/images/logo-zoho.png', alt: 'Zoho CRM' },
          { src: '/images/logo-salesforce.svg', alt: 'Salesforce' },
          { src: '/images/logo-pipedrive.png', alt: 'Pipedrive' },
          { src: '/images/logo-highlevel.png', alt: 'HighLevel' },
          { src: '/images/logo-keap.png', alt: 'Keap' },
          { src: '/images/logo-workiz.png', alt: 'Workiz' },
          { src: '/images/logo-housecallpro.png', alt: 'Housecall Pro' },
          { src: '/images/logo-servicetitan.png', alt: 'ServiceTitan' },
          { src: '/images/logo-jobtread.png', alt: 'JobTread' },
        ],
      },
    ],
  },
  {
    id: 'data-ops',
    n: '03',
    label: 'Data ops',
    sentence: 'Tracking, attribution, conversion sync, consent.',
    tabSub: 'Tracking, attribution, consent',
    catalogLabel: 'See all data ops services',
    catalogHref: '/services?segment=data-ops',
    stand: [
      {
        n: '01',
        title: 'No tracking',
        body: 'Tags are missing or guesswork. You need a measurement layer before anything else.',
        go: 'Implementation',
        href: '#get-quote',
      },
      {
        n: '02',
        title: 'Numbers do not match',
        body: 'GA4, ads, and the CRM each tell a different story.',
        go: 'Audit / Reset',
        href: '/audit',
      },
      {
        n: '03',
        title: 'Consent is blocking data',
        body: 'The CMP is live and conversions vanished. Tracking has to survive consent.',
        go: 'Consent fix',
        href: '#get-quote',
      },
      {
        n: '04',
        title: 'Need it maintained',
        body: 'The piping works. You need it kept accurate as tools and campaigns change.',
        go: 'Retainer',
        href: '#get-quote',
      },
    ],
    covers: [
      {
        n: '01',
        icon: 'track',
        title: 'Tracking',
        body: 'GA4, GTM, and server-side tagging so events fire once, correctly, and survive ad blockers.',
        logos: [
          { src: '/images/ga.png', alt: 'GA4' },
          { src: '/images/gtm.png', alt: 'Google Tag Manager' },
          { src: '/images/stape.png', alt: 'Stape' },
          { src: '/images/posthog.png', alt: 'PostHog' },
        ],
      },
      {
        n: '02',
        icon: 'attrib',
        title: 'Attribution and conversion sync',
        body: 'Source on every record, offline conversions back to Google Ads and Meta, so spend is judged on revenue.',
        logos: [
          { src: '/images/googleads.png', alt: 'Google Ads' },
          { src: '/images/facebook.png', alt: 'Meta' },
          { src: '/images/ga.png', alt: 'GA4' },
        ],
      },
      {
        n: '03',
        icon: 'consent',
        title: 'Consent',
        body: 'CMP wired so tracking still works after opt-in, and stays legal after opt-out.',
        logos: [
          favicon('cookieyes.com', 'CookieYes'),
          favicon('cookiebot.com', 'Cookiebot'),
          favicon('onetrust.com', 'OneTrust'),
        ],
      },
      {
        n: '04',
        icon: 'report',
        title: 'Reporting',
        body: 'Dashboards leadership can read without asking for a rebuild.',
        logos: [
          { src: '/images/looker.webp', alt: 'Looker' },
          { src: '/images/posthog.png', alt: 'PostHog' },
          { src: '/images/mixpanel.png', alt: 'Mixpanel' },
        ],
      },
    ],
  },
]

export const SMART_OPS_SMARTAI = {
  eyebrow: 'SmartAgents',
  title: 'Once the operations are built, SmartAgents runs them',
  href: '/ai',
} as const

export const OPS_CATALOG_SEGMENTS: {
  slug: OpsId
  label: string
  categories: string[]
}[] = [
  {
    slug: 'marketing-ops',
    label: 'Marketing ops',
    categories: ['marketing', 'seo', 'cro', 'ecommerce'],
  },
  {
    slug: 'sales-ops',
    label: 'Sales ops',
    categories: ['leadgen'],
  },
  {
    slug: 'data-ops',
    label: 'Data ops',
    categories: ['analytics'],
  },
]

export function catalogSegmentFromParams(segment?: string | null, category?: string | null) {
  if (segment && OPS_CATALOG_SEGMENTS.some((item) => item.slug === segment)) {
    return segment as OpsId
  }
  if (category) {
    const match = OPS_CATALOG_SEGMENTS.find(
      (item) => item.slug === category || item.categories.includes(category),
    )
    if (match) return match.slug
  }
  return OPS_CATALOG_SEGMENTS[0].slug
}
